export interface LogyxError {
  error: {
    message: string;
  };
  response: LogyxErrorResponse;
}

interface LogyxErrorResponse {
  data: {
    code: number;
    message: string;
    data: Record<string, unknown>;
    errors: string[];
  };
  status: number;
}

export const DEFAULT_ERROR_MESSAGE =
  'Oops! Something went wrong. Please refresh the page and try again.';

// Logyx core's error ids that Forms reads (logix-core docs/ordering.md, "Error
// ids"; flask_app/helpers/errors.py).
export const LogyxErrorId = {
  // Every "not found" of Logyx core's own, whatever was not found: its message
  // says what, translated (a cart, a shipping address, a company…).
  NOT_FOUND: 'A0:019',
  // An order it will not send again:
  // The order is still being sent: read its status later.
  ORDER_SENDING: 'A0:028',
  // It may already be at the supplier: UNKNOWN, being checked; or FAILED and not
  // sent again, or PENDING (set by hand), until the supplier checks it.
  // errors[0].status says which (readErrorStatus).
  ORDER_OUTCOME_UNKNOWN: 'A0:029',
  // An order from this cart is still being sent or checked; data.order_id names it.
  CART_ORDER_IN_PROGRESS: 'A0:030',
  // "Create new cart" refused: the cart's order failed, and can be sent again
  // (Retry, or Finish order with the cart); data.order_id names it.
  CART_ORDER_FAILED: 'A0:031',
} as const;

// One entry of an answer's `errors`: Logyx core's own error (an id and a
// message), or an order that did not go through (its id, status and reason).
export interface ILogyxErrorEntry {
  id?: string;
  message?: string;
  order_id?: number;
  status?: string;
}

// The fields of a failed answer's `data` that Forms reads.
export interface ILogyxErrorData {
  order_id?: number;
  status?: string;
  outcome_message?: string | null;
  message?: string;
}

export interface ILogyxErrorAnswer {
  // The HTTP status, when an answer came at all.
  status?: number;
  // The body is Logyx core's JSON. A proxy's HTML 504 page, a network error and
  // a timeout are not: the request may still have been carried out.
  isLogyxAnswer: boolean;
  errorId?: string;
  data?: ILogyxErrorData;
  errors: (ILogyxErrorEntry | string)[];
  message?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

// Reads a failed request without assuming its shape: callers must never throw
// while handling an error (react-query 3 then never ends the mutation, and its
// spinner runs forever).
export const readLogyxError = (error: unknown): ILogyxErrorAnswer => {
  const response =
    isRecord(error) && isRecord(error.response) ? error.response : undefined;
  const body = response?.data;
  const isLogyxAnswer =
    isRecord(body) && ('code' in body || 'errors' in body || 'data' in body);
  const logyx: Record<string, unknown> = isLogyxAnswer ? body : {};
  const errors = Array.isArray(logyx.errors)
    ? (logyx.errors as (ILogyxErrorEntry | string)[])
    : [];
  const first = errors[0];
  return {
    status: typeof response?.status === 'number' ? response.status : undefined,
    isLogyxAnswer,
    errorId:
      isRecord(first) && typeof first.id === 'string' ? first.id : undefined,
    data: isRecord(logyx.data) ? (logyx.data as ILogyxErrorData) : undefined,
    errors,
    message: typeof logyx.message === 'string' ? logyx.message : undefined,
  };
};

// The status of the order an answer's first error names. Logyx core's ordering
// sets it in every 409 that names an order and in a failed send's 400, whether
// `data` is an object (Finish order) or a list (Retry's own send).
export const readErrorStatus = (
  answer: ILogyxErrorAnswer
): string | undefined => {
  const first = answer.errors[0];
  return first && typeof first === 'object' ? first.status : undefined;
};

// No answer that says what happened: a network error, a timeout, a proxy's page
// (502, 503, 504) or a 5xx from Logyx itself. The request may have been carried
// out all the same.
export const isAnswerLost = (answer: ILogyxErrorAnswer): boolean =>
  !answer.isLogyxAnswer || answer.status === undefined || answer.status >= 500;

export const getToastErrorMessage = (error: LogyxError) => {
  if (error?.response?.data?.message === 'Unprocessable entity') {
    return handleUnprocessableEntityErrorMessage(error);
  } else {
    return handleGeneralErrorMessage(error);
  }
};

// TODO: revisit
// This handles errors of type flask marshmallow 422 ValidationErrors
// Pure UnprocessableEntity error responses will need to be handled differently
const handleUnprocessableEntityErrorMessage = (error: any) => {
  if (
    !error.response ||
    !error.response.data ||
    !error.response.data.errors ||
    error.response.data.errors.length === 0
  ) {
    return DEFAULT_ERROR_MESSAGE;
  }

  const errorMessages: string[] = [];

  error.response.data.errors.forEach((errorString: string) => {
    try {
      // Convert single-quoted JSON to valid JSON by replacing single quotes and parsing it
      const correctedJsonString = errorString.replace(/'/g, '"');
      const errorsJson = JSON.parse(correctedJsonString);

      // Extract messages from each key in the parsed object
      Object.keys(errorsJson).forEach((errorsKey: string) => {
        const messages = errorsJson[errorsKey];
        messages.forEach((message: string) => {
          errorMessages.push(`${errorsKey}: ${message}; \n`);
        });
      });
    } catch (e) {
      // Error parsing JSON
      return DEFAULT_ERROR_MESSAGE;
    }
  });

  const errorMessage = errorMessages.join(', ');

  return errorMessage || DEFAULT_ERROR_MESSAGE;
};

const handleGeneralErrorMessage = (error: any) => {
  const answer = readLogyxError(error);
  if (!answer.isLogyxAnswer) {
    return DEFAULT_ERROR_MESSAGE;
  }
  const first = answer.errors[0];
  const firstMessage =
    first && typeof first === 'object' ? first.message : first;
  return (
    (typeof firstMessage === 'string' && firstMessage) ||
    answer.message ||
    DEFAULT_ERROR_MESSAGE
  );
};
